import { useIntl } from 'react-intl';
import { useApolloClient } from '@apollo/client';
import { BILLING_PLAN_SLUG_ENUM } from '~/modules/projects/project/common/enums';
import { usePageOfProjectRate } from '~/modules/rateCard/advancedRateCard/components/hooks/usePageOfProjectRate';
import { GET_PORJECT_RATE_TABLE_DIMENSIONS } from '~/modules/rateCard/advancedRateCard/components/hooks';
import { setMilestoneTaskAvailable, setRateAvailable } from '../../helpers';
import setErrorTitle from '../utils';

import { useProjectTasksCount } from './useProjectTasksCount';

const showRateCardSetupRequiredError = ({ loading, projectRates }) =>
  !loading && !projectRates.length;

const showNoBillingRatesDefinedMessage = ({
  canViewBillingRates,
  canEditBillingRates,
  loading,
  projectRateCount
}) =>
  canViewBillingRates && !canEditBillingRates && !loading && !projectRateCount;

export const useBillPlanError = ({
  projectDetails,
  preRequisiteValues,
  billPlanSlug,
  canViewParams,
  canEditParams,
  isExistingBillPlan,
  canViewBillingRates,
  canEditBillingRates
}) => {
  const apolloClient = useApolloClient();
  const { projectId, projectSlug } = projectDetails;
  const cache = apolloClient.readQuery({
    query: GET_PORJECT_RATE_TABLE_DIMENSIONS,
    variables: {
      projectId
    }
  });

  const { loading, projectRates, projectRateCount } = usePageOfProjectRate({
    projectId,
    dimensionUris: (cache?.project?.rateTableDimension?.dimensions || []).map(
      d => d.id
    ),
    skip:
      !canViewBillingRates ||
      !(billPlanSlug === BILLING_PLAN_SLUG_ENUM.timeBilling)
  });

  const { tasksCount = 0, loadingTasksCount } = useProjectTasksCount({
    projectSlug,
    billPlanSlug
  });
  const { formatMessage } = useIntl();

  let errorTitle = '';
  let rateAvailable = false;
  let milestoneTaskAvailable = false;
  let hasViewRatePermission = true;
  let hasViewEditRatePermission = true;

  switch (billPlanSlug) {
    case BILLING_PLAN_SLUG_ENUM.fixedBidMilestone:
      milestoneTaskAvailable = setMilestoneTaskAvailable(preRequisiteValues);

      if (!milestoneTaskAvailable || loadingTasksCount) {
        return {
          errorTitle,
          milestoneTaskAvailable,
          rateAvailable,
          ratesCount: 0
        };
      }
      errorTitle = setErrorTitle({
        canViewParams,
        canEditParams,
        tasksCount,
        isExistingBillPlan,
        formatMessage
      });
      break;
    case BILLING_PLAN_SLUG_ENUM.timeBilling:
      rateAvailable = setRateAvailable(preRequisiteValues);
      if (!canViewBillingRates && !canEditBillingRates) {
        errorTitle = formatMessage({
          id: 'billPlanPreRequisites.noViewEditRatePermission'
        });
        hasViewEditRatePermission = false;

        return {
          errorTitle,
          milestoneTaskAvailable,
          rateAvailable,
          hasViewRatePermission,
          hasViewEditRatePermission
        };
      }

      if (
        showNoBillingRatesDefinedMessage({
          canViewBillingRates,
          canEditBillingRates,
          loading,
          projectRateCount
        })
      ) {
        errorTitle = formatMessage({
          id: 'billPlanPreRequisites.noBillingRatesDefinedMessage'
        });
        hasViewRatePermission = false;

        return {
          errorTitle,
          milestoneTaskAvailable,
          rateAvailable,
          hasViewRatePermission,
          hasViewEditRatePermission
        };
      }

      errorTitle = showRateCardSetupRequiredError({ loading, projectRates })
        ? formatMessage({
            id: 'billPlanPreRequisites.rateCardSetupRequired'
          })
        : '';
      break;
    default:
      break;
  }

  return {
    errorTitle,
    milestoneTaskAvailable,
    rateAvailable,
    ratesCount: projectRateCount,
    hasViewRatePermission,
    hasViewEditRatePermission
  };
};

export default useBillPlanError;
